import React from 'react';
import PublicHeader from '../components/common/PublicHeader';
import './WhyJustPractice.css';

const WhyJustPractice = () => {
  return (
    <div className="why-just-practice">
      <PublicHeader />

      {/* Hero */}
      <header className="hero">
        <div className="hero-content">
          <div className="logo-text">Just<span>Practice</span></div>
          <div className="hero-subtitle">Legal Practice Management</div>
          <p className="hero-tagline">
            Modern practice management software designed for how small law firms actually work.
          </p>
        </div>
      </header>

      {/* Who It's For */}
      <section className="audience">
        <div className="container">
          <div className="section-label">Built For You</div>
          <div className="section-title">Designed for small firms and solo practitioners</div>
          <p className="section-desc">
            The right balance of capability and simplicity &mdash; powerful enough to run your practice, intuitive enough to start using on day one.
          </p>
          <div className="audience-grid">
            <div className="audience-card">
              <div className="audience-icon">&#9878;</div>
              <h3>Solo Practitioners</h3>
              <p>One platform for your calendar, clients, cases, billing, and communications &mdash; so you can focus on practicing law.</p>
            </div>
            <div className="audience-card">
              <div className="audience-icon">&#9879;</div>
              <h3>Small Firms (2&ndash;10)</h3>
              <p>Keep your team aligned with real-time dashboards, workload visibility, and trust account oversight &mdash; without enterprise complexity.</p>
            </div>
            <div className="audience-card">
              <div className="audience-icon">&#9881;</div>
              <h3>Managing Partners</h3>
              <p>Financial visibility without micromanaging. See which cases need attention and how your team&rsquo;s capacity is tracking &mdash; at a glance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section>
        <div className="container">
          <div className="section-label">Capabilities</div>
          <div className="section-title">Everything your firm needs to run efficiently</div>

          <div className="features-grid">

            <div className="feature-card">
              <div className="feature-header">
                <div className="feature-icon cal">&#128197;</div>
                <h3>Calendar &amp; Scheduling</h3>
              </div>
              <p>Your calendar is the center of your practice, not an afterthought. Two-way Google and Outlook sync, conflict detection, and intelligent scheduling built around your actual workday.</p>
              <ul className="feature-bullets">
                <li>Click any time slot to create events instantly</li>
                <li>Court dates, prep time, and travel time tracking</li>
                <li>AI-suggested time slots and deadline-driven scheduling</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-header">
                <div className="feature-icon case">&#128188;</div>
                <h3>Cases, Projects &amp; Tasks</h3>
              </div>
              <p>Organize every matter into cases, projects, and tasks. Track progress, deadlines, and billable hours at every level &mdash; from firm-wide down to individual assignments.</p>
              <ul className="feature-bullets">
                <li>Reusable templates for common workflows</li>
                <li>Per-project value tracking and financial forecasting</li>
                <li>Priority, status, and deadline management</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-header">
                <div className="feature-icon trust">&#127974;</div>
                <h3>Trust Account Management</h3>
              </div>
              <p>IOLTA-compliant trust accounting with case-level fund allocations, balance alerts, and complete audit trails &mdash; built to meet bar compliance requirements.</p>
              <ul className="feature-bullets">
                <li>Case-level fund allocations and utilization tracking</li>
                <li>Low balance and frozen account alerts</li>
                <li>Weekly and monthly balance trend analysis</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-header">
                <div className="feature-icon inv">&#128196;</div>
                <h3>Invoicing &amp; Billing</h3>
              </div>
              <p>Generate invoices directly from tracked time entries. Professional PDF invoices, payment status tracking, and automatic late fee calculation.</p>
              <ul className="feature-bullets">
                <li>Billable vs. non-billable time classification</li>
                <li>LawPay integration for online payments</li>
                <li>Firm and individual billing goal tracking</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-header">
                <div className="feature-icon ai">&#10024;</div>
                <h3>AI-Powered Case Analysis</h3>
              </div>
              <p>Get intelligent insights on any case. The AI reviews the full picture &mdash; client info, case history, tasks, communications, and financials &mdash; for analysis that&rsquo;s actually useful.</p>
              <ul className="feature-bullets">
                <li>Context-aware conversations per case</li>
                <li>Persistent chat history for ongoing analysis</li>
                <li>Powered by Claude, Anthropic&rsquo;s frontier AI</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-header">
                <div className="feature-icon comm">&#128172;</div>
                <h3>Unified Communications</h3>
              </div>
              <p>SMS and email in one place, threaded by client. Full conversation history without switching between apps.</p>
              <ul className="feature-bullets">
                <li>Two-way SMS messaging via Twilio</li>
                <li>Gmail integration for email</li>
                <li>Complete communication history per client</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-header">
                <div className="feature-icon doc">&#128221;</div>
                <h3>Documents &amp; E-Signatures</h3>
              </div>
              <p>Store, organize, and sign documents without leaving the platform. Built-in e-signature support with reusable templates and multi-signer workflows.</p>
              <ul className="feature-bullets">
                <li>Cloud document storage with folder organization</li>
                <li>E-signatures with status tracking</li>
                <li>Full-text document search</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-header">
                <div className="feature-icon dash">&#128202;</div>
                <h3>Dashboards &amp; Reporting</h3>
              </div>
              <p>At-a-glance insights the moment you log in. Overdue tasks, court deadlines, case progress, workload metrics, and financial projections &mdash; all in one view.</p>
              <ul className="feature-bullets">
                <li>Personal and firm-wide view toggle</li>
                <li>Revenue, billing, and trust reports</li>
                <li>Team capacity and utilization tracking</li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* Why JustPractice */}
      <section className="why-section">
        <div className="container">
          <div className="section-label">Why JustPractice</div>
          <div className="section-title">Built for the way small firms actually operate</div>

          <div className="why-grid">
            <div className="why-card">
              <div className="why-icon">&#128161;</div>
              <h3>Simple by Design</h3>
              <p>Practical defaults and an intuitive interface mean your team is productive from day one &mdash; no week-long onboarding required.</p>
            </div>
            <div className="why-card">
              <div className="why-icon">&#128274;</div>
              <h3>Secure &amp; Compliant</h3>
              <p>Field-level encryption, role-based access control, complete audit trails, and firm-scoped data isolation. Your clients&rsquo; data stays protected.</p>
            </div>
            <div className="why-card">
              <div className="why-icon">&#9889;</div>
              <h3>Actively Developed</h3>
              <p>Built on a modern stack with regular updates. New features ship consistently based on feedback from firms like yours.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA / Footer */}
      <section className="cta">
        <div className="container">
          <div className="section-title">Ready to simplify your practice?</div>
          <p>Get started with a free trial. No credit card required.</p>
          <a href="/pricing" className="cta-button">View Pricing</a>
          <div className="footer-note">
            &copy; {new Date().getFullYear()} JustPractice. All rights reserved.
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyJustPractice;
